import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import {
  AcceptInvitationPage,
  ForgotPasswordPage,
  LoginPage,
  ResetPasswordPage,
  VerifyEmailPage,
  SignupPage,
} from '../pages/auth/AuthPages';
import { RequireAuth } from './Layout';
import { NotFoundPage } from './NotFoundPage';

// Cada módulo se carga bajo demanda (bundles pequeños).
const page = <M extends Record<string, unknown>>(loader: () => Promise<M>, name: keyof M) =>
  lazy(() => loader().then((m) => ({ default: m[name] as React.ComponentType })));

const Dashboard = page(() => import('../pages/dashboard/DashboardPage'), 'DashboardPage');
const Reports = page(() => import('../pages/reports/ReportsPage'), 'ReportsPage');
const Products = page(() => import('../pages/catalog/ProductsPage'), 'ProductsPage');
const Locations = page(() => import('../pages/inventory/LocationsPages'), 'LocationsPage');
const WarehouseHome = page(() => import('../pages/inventory/LocationsPages'), 'WarehouseHomePage');
const BinScan = page(() => import('../pages/inventory/LocationsPages'), 'BinScanPage');
const Plan = page(() => import('../pages/inventory/PlanPage'), 'PlanPage');
const LabelFormats = page(() => import('../pages/inventory/LocationsPages'), 'LabelFormatsPage');
const LabelPrint = page(() => import('../pages/inventory/labels'), 'LabelPrintPage');
const PickListPrint = page(() => import('../pages/inventory/PickList'), 'PickListPrintPage');
const StatusShares = page(() => import('../pages/catalog/StatusSharesPage'), 'StatusSharesPage');
const ProductForm = page(() => import('../pages/catalog/ProductFormPage'), 'ProductFormPage');
const CatalogSetup = page(() => import('../pages/catalog/CatalogSetupPage'), 'CatalogSetupPage');
const PriceLists = page(() => import('../pages/catalog/PriceListsPage'), 'PriceListsPage');
const Stock = page(() => import('../pages/inventory/InventoryPages'), 'StockPage');
const Movements = page(() => import('../pages/inventory/InventoryPages'), 'MovementsPage');
const Adjust = page(() => import('../pages/inventory/InventoryPages'), 'AdjustPage');
const Transfer = page(() => import('../pages/inventory/InventoryPages'), 'TransferPage');
const Warehouses = page(() => import('../pages/inventory/InventoryPages'), 'WarehousesPage');
const Customers = page(() => import('../pages/partners/PartnersPages'), 'CustomersPage');
const BusinessTypes = page(() => import('../pages/shared/BusinessLists'), 'BusinessTypesPage');
const Regions = page(() => import('../pages/shared/BusinessLists'), 'RegionsPage');
const Proposals = page(() => import('../pages/services/ProposalsPage'), 'ProposalsPage');
const ProposalDetail = page(() => import('../pages/services/ProposalsPage'), 'ProposalDetailPage');
const Projects = page(() => import('../pages/services/ProjectsPage'), 'ProjectsPage');
const ProjectDetail = page(() => import('../pages/services/ProjectsPage'), 'ProjectDetailPage');
const TimeEntries = page(() => import('../pages/services/TimePage'), 'TimePage');
const ServicesReports = page(
  () => import('../pages/services/ServicesReportsPage'),
  'ServicesReportsPage',
);
const ServicesSettings = page(
  () => import('../pages/settings/ServicesSettingsPage'),
  'ServicesSettingsPage',
);
const Assistant = page(() => import('../pages/assistant/AssistantPage'), 'AssistantPage');
const Contacts = page(() => import('../pages/partners/ContactsPage'), 'ContactsPage');
const CustomerStatement = page(() => import('../pages/partners/StatementPage'), 'StatementPage');
const Suppliers = page(() => import('../pages/partners/PartnersPages'), 'SuppliersPage');
const Orders = page(() => import('../pages/sales/OrdersPage'), 'OrdersPage');
const NewOrder = page(() => import('../pages/sales/NewOrderPage'), 'NewOrderPage');
const EditOrder = page(() => import('../pages/sales/NewOrderPage'), 'EditOrderPage');
const OrderDetail = page(() => import('../pages/sales/OrderDetailPage'), 'OrderDetailPage');
const Invoices = page(() => import('../pages/sales/FinancePages'), 'InvoicesPage');
const Payments = page(() => import('../pages/sales/FinancePages'), 'PaymentsPage');
const Returns = page(() => import('../pages/sales/FinancePages'), 'ReturnsPage');
const Receipts = page(() => import('../pages/sales/ReceiptsPage'), 'ReceiptsPage');
const Accounting = page(() => import('../pages/accounting/AccountingPages'), 'AccountingPage');
const Storefront = page(() => import('../pages/storefront/StorefrontPage'), 'StorefrontPage');
const PublicQuote = page(() => import('../pages/storefront/QuotePage'), 'QuotePage');
const PublicProposal = page(() => import('../pages/storefront/ProposalPage'), 'ProposalPage');
const WidgetChat = page(() => import('../pages/widget/WidgetPage'), 'WidgetPage');
const Inbox = page(() => import('../pages/inbox/InboxPage'), 'InboxPage');
const Pipeline = page(() => import('../pages/crm/CrmPages'), 'PipelinePage');
const CrmActivities = page(() => import('../pages/crm/CrmPages'), 'ActivitiesPage');
const CrmCalendar = page(() => import('../pages/crm/CalendarPage'), 'CalendarPage');
const CrmTeam = page(() => import('../pages/crm/TeamDashboardPage'), 'TeamDashboardPage');
const Channels = page(() => import('../pages/settings/ChannelsPage'), 'ChannelsPage');
const AiAgent = page(() => import('../pages/ai-agent/AiAgentPage'), 'AiAgentPage');
const AgentSettings = page(
  () => import('../pages/ai-agent/AgentSettingsPage'),
  'AgentSettingsPage',
);
const AgentPlans = page(() => import('../pages/ai-agent/AgentPlansPage'), 'AgentPlansPage');
const AgentCatalog = page(() => import('../pages/ai-agent/AgentCatalogPage'), 'AgentCatalogPage');
const Security = page(() => import('../pages/settings/SecurityPage'), 'SecurityPage');
const NotificationsSettings = page(
  () => import('../pages/settings/NotificationsSettingsPage'),
  'NotificationsSettingsPage',
);
const DataTransfer = page(() => import('../pages/settings/DataTransferPage'), 'DataTransferPage');
const PurchaseOrders = page(
  () => import('../pages/purchasing/PurchasingPages'),
  'PurchaseOrdersPage',
);
const NewPurchaseOrder = page(
  () => import('../pages/purchasing/PurchasingPages'),
  'NewPurchaseOrderPage',
);
const EditPurchaseOrder = page(
  () => import('../pages/purchasing/PurchasingPages'),
  'EditPurchaseOrderPage',
);
const PurchaseOrderDetail = page(
  () => import('../pages/purchasing/PurchasingPages'),
  'PurchaseOrderDetailPage',
);
const Treasury = page(() => import('../pages/treasury/TreasuryPages'), 'TreasuryPage');
const Expenses = page(() => import('../pages/treasury/TreasuryPages'), 'ExpensesPage');
const StockCounts = page(() => import('../pages/inventory/StockCountsPage'), 'StockCountsPage');
const StockCountDetail = page(
  () => import('../pages/inventory/StockCountsPage'),
  'StockCountDetailPage',
);
const TaxReports = page(() => import('../pages/reports/TaxReportsPage'), 'TaxReportsPage');
const Integrations = page(() => import('../pages/settings/IntegrationsPage'), 'IntegrationsPage');
const HelpCenter = page(() => import('../pages/help/HelpPages'), 'HelpCenterPage');
const Support = page(() => import('../pages/help/SupportPages'), 'SupportPage');
const PlatformSupport = page(() => import('../pages/help/SupportPages'), 'PlatformSupportPage');
const PlatformAgentRequests = page(
  () => import('../pages/platform/PlatformAgentRequestsPage'),
  'PlatformAgentRequestsPage',
);
const PlatformIntegrations = page(
  () => import('../pages/platform/PlatformIntegrationsPage'),
  'PlatformIntegrationsPage',
);
const PlatformEmailTemplates = page(
  () => import('../pages/platform/PlatformEmailTemplatesPage'),
  'PlatformEmailTemplatesPage',
);
const Company = page(() => import('../pages/settings/CompanyPage'), 'CompanyPage');
const Team = page(() => import('../pages/settings/TeamPages'), 'TeamPage');
const Roles = page(() => import('../pages/settings/TeamPages'), 'RolesPage');
const Billing = page(() => import('../pages/settings/BillingPage'), 'BillingPage');
const Gateways = page(() => import('../pages/settings/DeveloperPages'), 'GatewaysPage');
const ApiKeys = page(() => import('../pages/settings/DeveloperPages'), 'ApiKeysPage');
const Webhooks = page(() => import('../pages/settings/DeveloperPages'), 'WebhooksPage');
const Audit = page(() => import('../pages/settings/AuditPage'), 'AuditPage');
const EInvoicing = page(() => import('../pages/settings/EInvoicingPage'), 'EInvoicingPage');
const PlatformTenants = page(
  () => import('../pages/platform/PlatformPages'),
  'PlatformTenantsPage',
);
const PlatformPlans = page(
  () => import('../pages/platform/PlatformCatalogPages'),
  'PlatformCatalogPage',
);
const PlatformAddons = page(
  () => import('../pages/platform/PlatformCatalogPages'),
  'PlatformAddonsPage',
);
const PlatformMetrics = page(
  () => import('../pages/platform/PlatformPages'),
  'PlatformMetricsPage',
);
const PlatformAdmins = page(() => import('../pages/platform/PlatformPages'), 'PlatformAdminsPage');

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/accept-invitation', element: <AcceptInvitationPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  {
    path: '/cotizacion/:token',
    element: (
      <Suspense fallback={null}>
        <PublicQuote />
      </Suspense>
    ),
  },
  {
    path: '/propuesta/:token',
    element: (
      <Suspense fallback={null}>
        <PublicProposal />
      </Suspense>
    ),
  },
  {
    // Chat del agente dentro del iframe de la burbuja que la empresa pone en su página.
    path: '/widget/:key',
    element: (
      <Suspense fallback={null}>
        <WidgetChat />
      </Suspense>
    ),
  },
  {
    // Vista de impresión de etiquetas (sin el marco del panel: solo las etiquetas).
    path: '/imprimir/etiquetas',
    element: (
      <Suspense fallback={null}>
        <LabelPrint />
      </Suspense>
    ),
  },
  {
    // Lista de alistamiento de un pedido para imprimir (sin el marco del panel).
    path: '/imprimir/alistamiento',
    element: (
      <Suspense fallback={null}>
        <PickListPrint />
      </Suspense>
    ),
  },
  {
    path: '/tienda/:token',
    element: (
      <Suspense fallback={null}>
        <Storefront />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      { index: true, element: <Dashboard /> },
      { path: 'reports', element: <Reports /> },
      { path: 'catalog/products', element: <Products /> },
      { path: 'catalog/products/new', element: <ProductForm /> },
      { path: 'catalog/products/:id', element: <ProductForm /> },
      { path: 'catalog/status-shares', element: <StatusShares /> },
      { path: 'catalog/setup', element: <CatalogSetup /> },
      { path: 'catalog/price-lists', element: <PriceLists /> },
      { path: 'inventory/stock', element: <Stock /> },
      { path: 'inventory/movements', element: <Movements /> },
      { path: 'inventory/adjust', element: <Adjust /> },
      { path: 'inventory/transfer', element: <Transfer /> },
      { path: 'inventory/warehouses', element: <Warehouses /> },
      { path: 'customers', element: <Customers /> },
      { path: 'contacts', element: <Contacts /> },
      { path: 'customers/:id/statement', element: <CustomerStatement /> },
      { path: 'suppliers', element: <Suppliers /> },
      { path: 'sales/orders', element: <Orders /> },
      { path: 'sales/orders/new', element: <NewOrder /> },
      { path: 'sales/orders/:id/edit', element: <EditOrder /> },
      { path: 'sales/orders/:id', element: <OrderDetail /> },
      { path: 'sales/invoices', element: <Invoices /> },
      { path: 'sales/payments', element: <Payments /> },
      { path: 'sales/returns', element: <Returns /> },
      { path: 'sales/receipts', element: <Receipts /> },
      { path: 'sales/regions', element: <Regions /> },
      { path: 'accounting', element: <Accounting /> },
      { path: 'treasury', element: <Treasury /> },
      { path: 'expenses', element: <Expenses /> },
      { path: 'reports/tax', element: <TaxReports /> },
      { path: 'inventory/counts', element: <StockCounts /> },
      { path: 'inventory/locations', element: <Locations /> },
      { path: 'inventory/plan', element: <Plan /> },
      { path: 'bodega', element: <WarehouseHome /> },
      { path: 'bodega/u/:id', element: <BinScan /> },
      { path: 'settings/label-formats', element: <LabelFormats /> },
      { path: 'inventory/counts/:id', element: <StockCountDetail /> },
      { path: 'settings/integrations', element: <Integrations /> },
      { path: 'platform/integrations', element: <PlatformIntegrations /> },
      { path: 'platform/email-templates', element: <PlatformEmailTemplates /> },
      { path: 'platform/agent-requests', element: <PlatformAgentRequests /> },
      { path: 'platform/support', element: <PlatformSupport /> },
      { path: 'help', element: <HelpCenter /> },
      { path: 'help/:slug', element: <HelpCenter /> },
      { path: 'support', element: <Support /> },
      { path: 'purchasing/orders', element: <PurchaseOrders /> },
      { path: 'purchasing/orders/new', element: <NewPurchaseOrder /> },
      { path: 'purchasing/orders/:id/edit', element: <EditPurchaseOrder /> },
      { path: 'purchasing/orders/:id', element: <PurchaseOrderDetail /> },
      { path: 'settings/company', element: <Company /> },
      { path: 'settings/team', element: <Team /> },
      // Antes en Configuración: los enlaces viejos llevan a su sitio nuevo.
      { path: 'settings/commercial', element: <Navigate to="/crm/business-types" replace /> },
      { path: 'settings/services', element: <Navigate to="/services/settings" replace /> },
      { path: 'services/proposals', element: <Proposals /> },
      { path: 'services/proposals/:id', element: <ProposalDetail /> },
      { path: 'services/projects', element: <Projects /> },
      { path: 'services/projects/:id', element: <ProjectDetail /> },
      { path: 'services/time', element: <TimeEntries /> },
      { path: 'services/reports', element: <ServicesReports /> },
      { path: 'services/settings', element: <ServicesSettings /> },
      { path: 'assistant', element: <Assistant /> },
      { path: 'settings/roles', element: <Roles /> },
      { path: 'settings/data', element: <DataTransfer /> },
      { path: 'inbox', element: <Inbox /> },
      { path: 'inbox/:id', element: <Inbox /> },
      { path: 'crm', element: <Pipeline /> },
      { path: 'crm/activities', element: <CrmActivities /> },
      { path: 'crm/calendar', element: <CrmCalendar /> },
      { path: 'crm/team', element: <CrmTeam /> },
      { path: 'crm/business-types', element: <BusinessTypes /> },
      { path: 'settings/channels', element: <Channels /> },
      { path: 'ai-agent', element: <AiAgent /> },
      { path: 'ai-agent/settings', element: <AgentSettings /> },
      { path: 'ai-agent/plans', element: <AgentPlans /> },
      { path: 'ai-agent/catalog', element: <AgentCatalog /> },
      { path: 'settings/ai-agent', element: <Navigate to="/ai-agent" replace /> },
      { path: 'settings/security', element: <Security /> },
      { path: 'settings/notifications', element: <NotificationsSettings /> },
      { path: 'settings/billing', element: <Billing /> },
      { path: 'settings/gateways', element: <Gateways /> },
      { path: 'settings/api-keys', element: <ApiKeys /> },
      { path: 'settings/webhooks', element: <Webhooks /> },
      { path: 'settings/audit', element: <Audit /> },
      { path: 'settings/einvoicing', element: <EInvoicing /> },
      { path: 'platform/tenants', element: <PlatformTenants /> },
      { path: 'platform/plans', element: <PlatformPlans /> },
      { path: 'platform/addons', element: <PlatformAddons /> },
      { path: 'platform/metrics', element: <PlatformMetrics /> },
      { path: 'platform/admins', element: <PlatformAdmins /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
